import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import { 
    ArrowLeft, Sparkles, Wrench, Bus, Scissors, Package,
    ClipboardList, Phone, Clock, ChevronRight, Plus, CheckCircle2,
    AlertCircle, Send
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ServicesPage() {
    const navigate = useNavigate();
    const { organization } = useOrganization();
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('request');
    const [loading, setLoading] = useState(true);
    
    // Data states
    const [myRequests, setMyRequests] = useState([]);
    const [myAppointments, setMyAppointments] = useState([]);
    
    // Form state
    const [requestType, setRequestType] = useState('');
    const [requestTitle, setRequestTitle] = useState('');
    const [requestDetails, setRequestDetails] = useState('');
    const [requestPriority, setRequestPriority] = useState('normal');
    
    // Appointment modal
    const [appointmentModal, setAppointmentModal] = useState(false);
    const [appointmentForm, setAppointmentForm] = useState({
        service_type: 'hair_salon',
        date: '',
        time: '10:00',
        notes: ''
    });

    const headers = useCallback(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

    const serviceCategories = [
        { id: 'housekeeping', title: 'Housekeeping', icon: Sparkles, description: 'Cleaning, laundry, linen changes' },
        { id: 'maintenance', title: 'Maintenance', icon: Wrench, description: 'Repairs, technical issues, fixtures' },
        { id: 'hair_beauty', title: 'Hair & Beauty', icon: Scissors, description: 'Salon appointments, treatments' },
        { id: 'parcels', title: 'Parcels', icon: Package, description: 'Deliveries, collections, notifications' },
    ];

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [requestsRes, appointmentsRes] = await Promise.all([
                axios.get(`${API}/services/requests`, { headers: headers() }),
                axios.get(`${API}/services/appointments`, { headers: headers() })
            ]);
            setMyRequests(requestsRes.data);
            setMyAppointments(appointmentsRes.data);
        } catch (error) {
            console.error('Error fetching services data:', error);
        } finally {
            setLoading(false);
        }
    }, [headers]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSubmitRequest = async () => {
        if (!requestType || !requestTitle) {
            toast.error('Please select a service type and provide a title');
            return;
        }
        
        try {
            await axios.post(`${API}/services/requests`, {
                category: requestType,
                title: requestTitle,
                description: requestDetails,
                priority: requestPriority
            }, { headers: headers() });
            
            toast.success('Service request submitted!');
            setRequestType('');
            setRequestTitle('');
            setRequestDetails('');
            setRequestPriority('normal');
            setActiveTab('history');
            fetchData();
        } catch (error) {
            toast.error('Failed to submit request');
        }
    };

    const handleBookAppointment = async () => {
        if (!appointmentForm.date || !appointmentForm.time) {
            toast.error('Please select a date and time');
            return;
        }
        
        try {
            await axios.post(`${API}/services/appointments`, appointmentForm, { headers: headers() });
            toast.success('Appointment booked!');
            setAppointmentModal(false);
            setAppointmentForm({ service_type: 'hair_salon', date: '', time: '10:00', notes: '' });
            fetchData();
        } catch (error) {
            toast.error('Failed to book appointment');
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            submitted: 'bg-amber-100 text-amber-700',
            acknowledged: 'bg-blue-100 text-blue-700',
            in_progress: 'bg-purple-100 text-purple-700',
            completed: 'bg-green-100 text-green-700',
            cancelled: 'bg-gray-100 text-gray-500',
        };
        return colors[status] || 'bg-gray-100 text-gray-600';
    };

    const getStatusIcon = (status) => {
        if (status === 'completed') return <CheckCircle2 className="w-4 h-4" />;
        if (status === 'in_progress') return <Clock className="w-4 h-4" />;
        return <AlertCircle className="w-4 h-4" />;
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            weekday: 'short', day: 'numeric', month: 'short'
        });
    };

    const formatDateTime = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-stone-50 pb-24" data-testid="services-page">
            {/* Header */}
            <div className="bg-gradient-to-br from-blue-700 to-blue-800 text-white px-6 pt-6 pb-8">
                <button 
                    onClick={() => navigate('/home')}
                    className="flex items-center gap-2 text-blue-200 hover:text-white mb-4"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Home
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Sparkles className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-serif text-white">Services & Concierge</h1>
                        <p className="text-white/80 text-sm">Requests, bookings & assistance</p>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="px-4 -mt-4 relative z-10">
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-1 flex">
                    {['request', 'appointments', 'history'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                                activeTab === tab
                                    ? 'bg-blue-600 text-white'
                                    : 'text-stone-600 hover:bg-stone-100'
                            }`}
                        >
                            {tab === 'request' ? 'New Request' : tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="px-4 mt-6">
                {/* Minibus Trips Banner */}
                <div 
                    onClick={() => navigate('/transport')}
                    className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-4 mb-6 cursor-pointer hover:from-teal-700 hover:to-teal-800 transition-all"
                    data-testid="transport-banner"
                >
                    <div className="flex items-center justify-between text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                                <Bus className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="font-semibold">Minibus Trips</h3>
                                <p className="text-teal-100 text-sm">Book your seat on community outings</p>
                            </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-teal-200" />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : (
                    <>
                        {/* New Request Tab */}
                        {activeTab === 'request' && (
                            <div className="space-y-6">
                                <div>
                                    <h2 className="text-lg font-semibold text-stone-800 mb-4">What do you need help with?</h2>
                                    <div className="grid grid-cols-2 gap-3">
                                        {serviceCategories.map((cat) => (
                                            <button
                                                key={cat.id}
                                                onClick={() => setRequestType(cat.id)}
                                                className={`p-4 rounded-2xl border text-left transition-all ${
                                                    requestType === cat.id
                                                        ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200'
                                                        : 'bg-white border-stone-200 hover:border-blue-200'
                                                }`}
                                            >
                                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${
                                                    requestType === cat.id ? 'bg-blue-100' : 'bg-stone-100'
                                                }`}>
                                                    <cat.icon className={`w-5 h-5 ${requestType === cat.id ? 'text-blue-600' : 'text-stone-600'}`} />
                                                </div>
                                                <h3 className="font-medium text-stone-800 text-sm">{cat.title}</h3>
                                                <p className="text-xs text-stone-500 mt-0.5">{cat.description}</p>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {requestType && (
                                    <div className="bg-white rounded-2xl p-5 border border-stone-200 space-y-4">
                                        <h3 className="font-semibold text-stone-800">Request Details</h3>
                                        <div>
                                            <Label>Title / Brief Summary</Label>
                                            <Input
                                                value={requestTitle}
                                                onChange={(e) => setRequestTitle(e.target.value)}
                                                placeholder="e.g., Bathroom tap dripping"
                                                className="mt-1"
                                            />
                                        </div>
                                        <div>
                                            <Label>Details</Label>
                                            <Textarea
                                                placeholder="Please describe what you need..."
                                                value={requestDetails}
                                                onChange={(e) => setRequestDetails(e.target.value)}
                                                rows={4}
                                                className="mt-1"
                                            />
                                        </div>
                                        <div>
                                            <Label>Priority</Label>
                                            <select
                                                value={requestPriority}
                                                onChange={(e) => setRequestPriority(e.target.value)}
                                                className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg"
                                            >
                                                <option value="low">Low - When convenient</option>
                                                <option value="normal">Normal</option>
                                                <option value="high">High - Please prioritise</option>
                                                <option value="urgent">Urgent - As soon as possible</option>
                                            </select>
                                        </div>
                                        <Button 
                                            className="w-full bg-blue-600 hover:bg-blue-700"
                                            onClick={handleSubmitRequest}
                                        >
                                            <Send className="w-4 h-4 mr-2" />
                                            Submit Request
                                        </Button>
                                    </div>
                                )}

                                {/* AI Assistant Help */}
                                <div 
                                    onClick={() => navigate('/assistant')}
                                    className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-5 cursor-pointer hover:from-blue-700 hover:to-blue-800 transition-all"
                                    data-testid="ai-assistant-help"
                                >
                                    <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                                        <Phone className="w-5 h-5" />
                                        Need more assistance?
                                    </h3>
                                    <p className="text-blue-100 text-sm mb-3">
                                        Our AI assistant is available around the clock.
                                    </p>
                                    <span className="text-white font-bold flex items-center gap-2">
                                        Chat with Assistant
                                        <ChevronRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Appointments Tab */}
                        {activeTab === 'appointments' && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-lg font-semibold text-stone-800">Book an Appointment</h2>
                                    <Button 
                                        onClick={() => setAppointmentModal(true)}
                                        className="bg-blue-600 hover:bg-blue-700"
                                        size="sm"
                                    >
                                        <Plus className="w-4 h-4 mr-1" />
                                        Book
                                    </Button>
                                </div>
                                
                                {/* My Appointments */}
                                {myAppointments.length > 0 && (
                                    <div>
                                        <h3 className="font-medium text-stone-700 mb-3">Your Appointments</h3>
                                        <div className="space-y-3">
                                            {myAppointments.map((apt) => (
                                                <div key={apt.id} className="bg-white rounded-xl p-4 border border-stone-200">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 capitalize">
                                                                {apt.service_type.replace('_', ' ')}
                                                            </span>
                                                            <p className="font-medium text-stone-800 mt-1">
                                                                {formatDate(apt.date)} at {apt.time}
                                                            </p>
                                                            {apt.notes && (
                                                                <p className="text-sm text-stone-500">{apt.notes}</p>
                                                            )}
                                                        </div>
                                                        <span className={`text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(apt.status)}`}>
                                                            {apt.status}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Service options */}
                                <div className="space-y-3">
                                    {[
                                        { title: 'Hair Salon', type: 'hair_salon' },
                                        { title: 'Beauty Treatments', type: 'beauty' },
                                        { title: 'Physio/Massage', type: 'physio' },
                                    ].map((service) => (
                                        <div key={service.type} className="bg-white rounded-2xl p-4 border border-stone-200 flex items-center justify-between">
                                            <div>
                                                <h3 className="font-semibold text-stone-800">{service.title}</h3>
                                                <p className="text-sm text-stone-500">Book your preferred time</p>
                                            </div>
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => {
                                                    setAppointmentForm({ ...appointmentForm, service_type: service.type });
                                                    setAppointmentModal(true);
                                                }}
                                            >
                                                Book
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* History Tab */}
                        {activeTab === 'history' && (
                            <div className="space-y-4">
                                <h2 className="text-lg font-semibold text-stone-800 mb-4">Your Requests</h2>
                                
                                {myRequests.length === 0 ? (
                                    <div className="bg-stone-100 rounded-xl p-8 text-center">
                                        <ClipboardList className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                                        <p className="text-stone-500">No service requests yet</p>
                                    </div>
                                ) : (
                                    myRequests.map((request) => (
                                        <div key={request.id} className="bg-white rounded-2xl p-4 border border-stone-200">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 capitalize">
                                                            {request.category?.replace('_', ' ')}
                                                        </span>
                                                        {request.priority !== 'normal' && (
                                                            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${
                                                                request.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                                                                request.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                                                                'bg-gray-100 text-gray-600'
                                                            }`}>
                                                                {request.priority}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h3 className="font-semibold text-stone-800">{request.title}</h3>
                                                    {request.description && (
                                                        <p className="text-sm text-stone-500 mt-1">{request.description}</p>
                                                    )}
                                                    <p className="text-xs text-stone-400 mt-2">
                                                        Submitted {formatDateTime(request.created_at)}
                                                    </p>
                                                    
                                                    {/* Notes/Updates */}
                                                    {request.notes?.length > 0 && (
                                                        <div className="mt-3 pt-3 border-t border-stone-100">
                                                            <p className="text-xs font-medium text-stone-600 mb-2">Updates:</p>
                                                            {request.notes.map((note, idx) => (
                                                                <div key={idx} className="text-xs text-stone-500 mb-1">
                                                                    <span className="font-medium">{note.author}:</span> {note.note}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full capitalize ${getStatusColor(request.status)}`}>
                                                    {getStatusIcon(request.status)}
                                                    {request.status?.replace('_', ' ')}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Appointment Modal */}
            <Dialog open={appointmentModal} onOpenChange={setAppointmentModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Book Appointment</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Service</Label>
                            <select
                                value={appointmentForm.service_type}
                                onChange={(e) => setAppointmentForm({ ...appointmentForm, service_type: e.target.value })}
                                className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg"
                            >
                                <option value="hair_salon">Hair Salon</option>
                                <option value="beauty">Beauty Treatments</option>
                                <option value="physio">Physiotherapy</option>
                                <option value="massage">Massage</option>
                            </select>
                        </div>
                        <div>
                            <Label>Date</Label>
                            <Input
                                type="date"
                                value={appointmentForm.date}
                                onChange={(e) => setAppointmentForm({ ...appointmentForm, date: e.target.value })}
                                min={new Date().toISOString().split('T')[0]}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label>Time</Label>
                            <select
                                value={appointmentForm.time}
                                onChange={(e) => setAppointmentForm({ ...appointmentForm, time: e.target.value })}
                                className="w-full mt-1 px-3 py-2 border border-stone-200 rounded-lg"
                            >
                                {['09:00', '09:30', '10:00', '10:30', '11:00', '11:30', '14:00', '14:30', '15:00', '15:30', '16:00'].map(t => (
                                    <option key={t} value={t}>{t}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label>Notes (optional)</Label>
                            <Textarea
                                value={appointmentForm.notes}
                                onChange={(e) => setAppointmentForm({ ...appointmentForm, notes: e.target.value })}
                                placeholder="Any special requirements..."
                                rows={3}
                                className="mt-1"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAppointmentModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleBookAppointment} className="bg-blue-600 hover:bg-blue-700">
                            Book Appointment
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default ServicesPage;
